import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  CumulatedEnergyProperties,
  FeatureNames,
  IsEnabledProperty,
  IsNumberValueProperty,
  SaltLevelProperties,
  IsActiveProperty,
  Feature,
  HeatingDhwProperties,
  VentilationProgramProperties,
  VentilationModeCommands,
  VentilationModeProperties,
  DeviceIds,
  HeatingRodsHeatGenerationProperties,
} from '@vi-iot/client';

import {
  DevicesStore,
  FeatureService,
  FEATURES_PANEL_TYPES,
  GatewayStatus,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  CircuitType,
  CircuitDetails,
} from '@store';

import { InstallationService } from '../../services/installation.service';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';
import { Chart } from '../../../features/chart-bar/models/chart.model';

export class DeviceFeatureTilesBase {
  readonly HEATING_ZONE_PANEL = 'HeatingZone';
  readonly HEATING_CIRCUIT_PANEL = 'HeatingCircuit';
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  panelTypes = FEATURES_PANEL_TYPES;
  circuitType = CircuitType;
  FeatureNames = FeatureNames;
  availableCircuits: Array<string> = [];
  ngUnsubscribe: Subject<void> = new Subject<void>();

  installationId: number;

  // TODO VIG-3076 commented temporarily
  // defrosting$: Observable<Feature<IsActiveProperty>>;

  dhw$: Observable<Feature<HeatingDhwProperties, never>>;

  // Ventilation
  ventilationProgram$: Observable<Feature<VentilationProgramProperties, never>>;
  ventilationMode$: Observable<Feature<VentilationModeProperties, VentilationModeCommands>>;

  // Photovoltaics
  photovoltaicCumulatedEnergy$: Observable<Feature<CumulatedEnergyProperties, never>>;

  // Energy Cockpit
  cumulativeProduced$: Observable<Feature<IsNumberValueProperty, never>>;
  cumulativePurchased$: Observable<Feature<IsNumberValueProperty, never>>;
  cumulativeSold$: Observable<Feature<IsNumberValueProperty, never>>;

  // Solar
  solar$: Observable<Feature<IsActiveProperty, never>>;

  // Electric Heater
  electricHeater$: Observable<Feature<HeatingRodsHeatGenerationProperties, never>>;

  // Refrigerant Circuits
  enabledRefrigerantCircuits$: Observable<Feature<IsEnabledProperty, never>>;

  // EcoWater
  saltLevelDays$: Observable<Feature<SaltLevelProperties, never>>;
  isESSDevice$: Observable<boolean>;

  // Fuel cell
  isFuelCellDevice$: Observable<boolean>;

  // Heating circuits
  availableCircuitsDetails$: Observable<CircuitDetails[]>;

  gatewayStatus$: Observable<GatewayStatus>;

  // Charts
  heatProductionChartData$: Observable<Chart>;
  electricityConsumptionChartData$: Observable<Chart>;
  electricityConsumptionLegacyChartData$: Observable<Chart>;
  powerProductionChartData$: Observable<Chart>;
  solarPowerProductionChartData$: Observable<Chart>;
  gasConsumptionChartData$: Observable<Chart>;

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public devicesStore: DevicesStore,
    public featureService: FeatureService,
    public installationService: InstallationService,
    public installationFeaturesStore: InstallationFeaturesStore
  ) {}

  protected initializeDeviceFeatures(deviceSelectId: string, deviceId: string): void {
    this.electricHeater$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodsHeatGeneration,
      deviceId
    );
    this.dhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhw,
      deviceId
    );

    // Ventilation
    this.ventilationMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationMode,
      deviceId
    );
    this.ventilationProgram$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationProgram,
      deviceId
    );

    // Photovoltaics
    this.isESSDevice$ = this.devicesStore.isESSDevice(deviceSelectId);
    this.photovoltaicCumulatedEnergy$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicCumulatedEnergy,
      deviceId
    );

    // Energy Cockpit
    this.cumulativeProduced$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerCumulativeProduced,
      deviceId
    );
    this.cumulativePurchased$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerCumulativePurchased,
      deviceId
    );
    this.cumulativeSold$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerCumulativeSold,
      deviceId
    );

    // Solar
    this.solar$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.solar,
      deviceId
    );

    // Refrigerant Circuits
    this.enabledRefrigerantCircuits$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.compressors,
      deviceId
    );

    // Fuel cell
    this.isFuelCellDevice$ = this.devicesStore.isFuelCellDevice(deviceSelectId);

    // Heating circuits
    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsActiveProperty, {}>(
        FeatureNames.roomStatus,
        DeviceIds.RoomControl
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((feature) => {
        this.installationService.isSrcActive$.next(!!feature?.properties?.active?.value);
      });

    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.circuits)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((feature: Feature<IsEnabledProperty, never>) => {
        this.availableCircuits = feature?.properties?.enabled?.value ?? [];
      });

    this.availableCircuitsDetails$ = this.devicesStore
      .getAvailableCircuitsDetails(deviceId)
      .pipe(takeUntil(this.ngUnsubscribe));

    this.saltLevelDays$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ecoWaterSaltLevelDays
    );

    this.heatProductionChartData$ = this.featureService.selectHeatProductionChartData();
    this.electricityConsumptionChartData$ = this.featureService.selectPowerConsumptionChartData();
    this.powerProductionChartData$ = this.featureService.selectPowerProductionChartData();
    this.solarPowerProductionChartData$ = this.featureService.selectSolarPowerProductionChartData();
    this.gasConsumptionChartData$ = this.featureService.selectGasConsumptionChartData();
  }

  getHeatingCircuitPanelName(circuitId: string): string {
    return this.panelTypes[this.HEATING_CIRCUIT_PANEL + circuitId];
  }

  getTTCircuitPanelName(circuitId: string): string {
    // circuit 4 is TTcircuit 1
    const ttCircuitId = +circuitId - 3;

    return this.panelTypes[this.HEATING_ZONE_PANEL + ttCircuitId];
  }

  getCallForHeatHeatingZonePanelName(): string {
    return this.panelTypes[this.HEATING_ZONE_PANEL];
  }
}
